/**
 * Claim the invite code stored in this browser for the signed-in account (#2652).
 *
 * The code is stored on `/start`, and a signed-out redeem ties it to this
 * browser. Once the account is known, one signed-in redeem (idempotent)
 * ties it to the account. Then `/api/account/me` is read again so
 * `products_offered` carries the product before any page reads it.
 * The stored code is cleared once the claim is answered.
 *
 * `claiming` is true from the first signed-in render with a stored code
 * until the claim and the refresh are done.
 */
import { useEffect, useState } from "react";

import { useAuth } from "./AuthContext";
import { clearInviteCode, redeemInvite, storedInviteCode } from "./productInvite";

export function useClaimStoredInvite(): boolean {
  const { accountId, loading, refresh } = useAuth();
  const [claiming, setClaiming] = useState(() => storedInviteCode() !== null);

  useEffect(() => {
    if (loading) return;
    const code = storedInviteCode();
    if (!accountId || !code) {
      setClaiming(false);
      return;
    }
    let alive = true;
    setClaiming(true);
    redeemInvite(code)
      .then(async (key) => {
        if (key) await refresh({ silent: true });
        clearInviteCode();
      })
      .finally(() => {
        if (alive) setClaiming(false);
      });
    return () => {
      alive = false;
    };
  }, [accountId, loading, refresh]);

  return claiming;
}
